import type { ChangeEvent, ClipboardEvent, ComponentProps, Ref } from "react";

import { forwardRef, useLayoutEffect, useRef } from "react";

import { Input } from "@/components/ui/input";
import { caretAfterDigits, editedPhone, formatPhoneInput, pastedPhone, phoneDigits } from "@/utils/phone";

export type PhoneInputProps = Omit<ComponentProps<typeof Input>, "value" | "onChange" | "type"> & {
  value: string | null | undefined;
  /** Called with the digits only — what the form holds and the backend stores. */
  onChange: (digits: string) => void;
};

const assignRef = <T,>(ref: Ref<T> | undefined, node: T) => {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref) {
    (ref as { current: T }).current = node;
  }
};

/**
 * A phone number field. The value is kept as digits and shown dashed as it fills
 * up (514-825-7340); pasting drops everything but the digits first, so a copied
 * "+1 (514) 825-7340" lands as 5148257340.
 */
const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, onPaste, autoComplete, ...props },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // How many digits sit before the caret after an edit; the caret is put back
  // after them once the value is re-dashed.
  const caret = useRef<number | null>(null);

  const digits = phoneDigits(value);
  const shown = formatPhoneInput(digits);

  useLayoutEffect(() => {
    const input = inputRef.current;

    if (caret.current === null || !input || document.activeElement !== input) {
      return;
    }

    const position = caretAfterDigits(shown, caret.current);

    input.setSelectionRange(position, position);
    caret.current = null;
  }, [shown]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const edit = editedPhone({
      typed: event.target.value,
      caret: event.target.selectionStart ?? event.target.value.length,
      previous: digits,
      forward: (event.nativeEvent as InputEvent).inputType === "deleteContentForward",
    });

    caret.current = edit.digitsBeforeCaret;
    onChange(edit.digits);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    onPaste?.(event);

    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();

    const input = event.currentTarget;
    const edit = pastedPhone({
      shown: input.value,
      selectionStart: input.selectionStart ?? input.value.length,
      selectionEnd: input.selectionEnd ?? input.value.length,
      text: event.clipboardData.getData("text"),
    });

    caret.current = edit.digitsBeforeCaret;
    onChange(edit.digits);
  };

  return (
    <Input
      {...props}
      ref={(node: HTMLInputElement | null) => {
        inputRef.current = node;
        assignRef(ref, node);
      }}
      autoComplete={autoComplete ?? "tel"}
      inputMode="tel"
      type="tel"
      value={shown}
      onChange={handleChange}
      onPaste={handlePaste}
    />
  );
});

export default PhoneInput;
